import React from 'react';
import { MoreInfoOutlined } from '@duxton-web/icons';
import { Alert, Flex, Typography } from '@duxton-web/ui';

import JsonSchemaConverterModal from './JsonSchemaConverterModal';
import {
  JSON_SCHEMA_DRAFT_07_URL,
  JSON_SCHEMA_DRAFT_2019_09_URL,
  JSON_SCHEMA_REFERENCE_URL,
} from '@/constants/jsonSchema';

interface JsonSchemaConverterAlertProps {
  /**
   * Callback when schema is successfully generated
   */
  onSchemaGenerated: (schema: string) => void;

  /**
   * Whether the converter is disabled
   */
  disabled?: boolean;

  /**
   * Title for the generated schema
   */
  schemaTitle?: string;
}

/**
 * Reusable alert component that displays instructions for generating JSON Schema
 * from JSON data. Provides two options:
 * 1. Built-in converter (with button and modal)
 * 2. External tool (transform.tools)
 */
const JsonSchemaConverterAlert: React.FC<JsonSchemaConverterAlertProps> = ({
  onSchemaGenerated,
  disabled = false,
  schemaTitle,
}) => {
  if (disabled) return null;

  return (
    <Alert
      type="info"
      message={
        <Flex gap={8}>
          <MoreInfoOutlined
            style={{
              alignSelf: 'flex-start',
              color: '#136FD8',
              fontSize: 24,
            }}
          />
          <Flex vertical gap={12}>
            <Typography.Body level={2} strong>
              Generate JSON validation schema from your JSON data
            </Typography.Body>
            <Typography.Text>
              If you don&apos;t have a JSON Schema, you can generate one from
              your existing JSON in two ways:
            </Typography.Text>

            {/* Option 1: Built-in converter */}
            <Flex vertical gap={4} style={{ paddingLeft: '16px' }}>
              <Typography.Text>
                <strong>Option 1:</strong> Use our built-in converter
              </Typography.Text>
              <JsonSchemaConverterModal
                onSchemaGenerated={onSchemaGenerated}
                disabled={disabled}
                schemaTitle={schemaTitle}
              />
            </Flex>

            {/* Option 2: External tool */}
            <Flex vertical gap={4} style={{ paddingLeft: '16px' }}>
              <Typography.Text>
                <strong>Option 2:</strong> Use an external tool
              </Typography.Text>
              <Typography.Text>
                1. Navigate to{' '}
                <a
                  href="https://transform.tools/json-to-json-schema"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://transform.tools/json-to-json-schema
                </a>
                <br />
                2. Paste your JSON data
                <br />
                3. Copy the generated schema and paste it below
              </Typography.Text>
            </Flex>

            {/* Learn more */}
            <Typography.Text type="secondary">
              Learn more about{' '}
              <a
                href={JSON_SCHEMA_REFERENCE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                JSON Schema
              </a>
              . GrabX supports{' '}
              <a
                href={JSON_SCHEMA_DRAFT_07_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ whiteSpace: 'nowrap' }}
              >
                draft-07
              </a>{' '}
              (recommended) and{' '}
              <a
                href={JSON_SCHEMA_DRAFT_2019_09_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ whiteSpace: 'nowrap' }}
              >
                draft-2019-09{' '}
              </a>
              standards.
            </Typography.Text>
          </Flex>
        </Flex>
      }
    />
  );
};

export default JsonSchemaConverterAlert;
